import { supabase } from '../config/supabase';
import { logError } from './LoggingService';
import { isOnPremDeployment } from '../config/deploymentConfig';
import * as JWTAuth from './JWTAuthService';
import { coreServiceRequest } from '../utils/apiClient';

/**
 * Authentication Service
 *
 * Routes authentication operations to the appropriate implementation:
 * - Cloud deployment: Supabase Auth
 * - On-premises deployment: JWT Auth via Core API
 */

/**
 * Get current session
 * @returns {Promise<Object|null>} Session object or null if not authenticated
 */
export const getSession = async () => {
  if (isOnPremDeployment()) {
    return JWTAuth.getSession();
  }

  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    logError('Failed to get session', error, 'AuthService');
    return null;
  }
  return session;
};

/**
 * Refresh the current session
 * @returns {Promise<Object|null>} New session object or null if refresh failed
 */
export const refreshSession = async () => {
  if (isOnPremDeployment()) {
    return JWTAuth.refreshSession();
  }

  const { data: { session }, error } = await supabase.auth.refreshSession();
  if (error) {
    logError('Failed to refresh session', error, 'AuthService');
    return null;
  }
  return session;
};

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} Sign in result
 */
export const signIn = async (email, password) => {
  if (isOnPremDeployment()) {
    return JWTAuth.signIn(email, password);
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  return { data, error };
};

/**
 * Sign up with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {Object} metadata - Additional user metadata
 * @returns {Promise<Object>} Sign up result
 */
export const signUp = async (email, password, metadata = {}) => {
  if (isOnPremDeployment()) {
    return JWTAuth.signUp(email, password, metadata);
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
      emailRedirectTo: `${window.location.origin}/signin`
    }
  });
  return { data, error };
};

/**
 * Sign out current user
 * @returns {Promise<Object>} Sign out result
 */
export const signOut = async () => {
  if (isOnPremDeployment()) {
    return JWTAuth.signOut();
  }

  const { error } = await supabase.auth.signOut();
  return { error };
};

/**
 * Sign in with OAuth provider
 * @param {string} provider - OAuth provider (github, google, etc.)
 * @returns {Promise<Object>} OAuth sign in result
 */
export const signInWithOAuth = async (provider) => {
  if (isOnPremDeployment()) {
    return JWTAuth.signInWithOAuth(provider);
  }

  // Define minimal scopes per provider
  // GitHub: Don't specify scopes - use default which requests no additional permissions
  //         (identity verification only, email provided via public profile)
  // Google: Request minimal OIDC scopes for basic profile
  const providerScopes = {
    google: 'openid email profile'  // Google: OIDC with basic profile (name, email, picture)
  };

  // Construct proper redirect URL to login page
  // This ensures Supabase redirects back to /signin after OAuth where AuthForm can handle the saved redirect path
  const redirectUrl = `${window.location.origin}/signin`;

  const options = {
    redirectTo: redirectUrl
  };

  // Only add scopes if defined for the provider
  if (providerScopes[provider]) {
    options.scopes = providerScopes[provider];
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options
  });
  return { data, error };
};

/**
 * Reset password for email
 * @param {string} email - User email
 * @returns {Promise<Object>} Password reset result
 */
export const resetPassword = async (email) => {
  if (isOnPremDeployment()) {
    return JWTAuth.resetPassword(email);
  }

  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });
  return { data, error };
};

/**
 * Update user password
 * @param {string} password - New password
 * @returns {Promise<Object>} Password update result
 */
export const updatePassword = async (password) => {
  if (isOnPremDeployment()) {
    return JWTAuth.updatePassword(password);
  }

  const { data, error } = await supabase.auth.updateUser({
    password
  });
  return { data, error };
};

/**
 * Get current user
 * @returns {Promise<Object|null>} User object or null if not authenticated
 */
export const getCurrentUser = async () => {
  if (isOnPremDeployment()) {
    // For on-prem, get user from session
    const session = await JWTAuth.getSession();
    return session ? session.user : null;
  }

  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    logError('Failed to get user', error, 'AuthService');
    return null;
  }
  return user;
};

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Callback function for auth state changes
 * @returns {Object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  if (isOnPremDeployment()) {
    return JWTAuth.onAuthStateChange(callback);
  }

  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => {
      callback(event, session);
    }
  );
  return subscription;
};

/**
 * Delete the current user's account
 * @returns {Promise<void>}
 */
export const deleteUserAccount = async () => {
  // Use coreServiceRequest helper which handles auth tokens and uses the proxy
  const response = await coreServiceRequest('/api/user/account', {
    method: 'DELETE'
  });

  return response;
};

// Export supabase client for any direct auth operations not covered above
export { supabase };